import { useEffect, useState } from "react";

// Hooks & Helpers
import { useToast } from "sj-ui-toolkit/src/hooks";
import {
  fetchActions,
  saveActions,
  restoreActions,
  type ActivityAction,
  type ActivityObject,
} from "@/api/activities";

// 3rd Party Imports
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import { OrderList } from "primereact/orderlist";
import { confirmDialog } from "primereact/confirmdialog";

// Local Components
import TabSkeleton from "./TabSkeleton";

interface ActionsTabProps {
  onUnsavedChange?: (hasChanges: boolean) => void;
}

export default function ActionsTab({ onUnsavedChange }: ActionsTabProps) {
  const [actions, setActions] = useState<ActivityAction[]>([]);
  const [maxActionId, setMaxActionId] = useState(0);
  const [maxObjectId, setMaxObjectId] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedActions, setExpandedActions] = useState<Record<number, boolean>>({});

  const markChanged = () => onUnsavedChange?.(true);
  const markSaved = () => onUnsavedChange?.(false);

  const { showToast } = useToast();

  useEffect(() => {
    const loadActions = async () => {
      try {
        setIsLoading(true);
        const data = await fetchActions();
        setActions(data.actions || []);
        setMaxActionId(data.maxActionId || 0);
        setMaxObjectId(data.maxObjectId || 0);
        markSaved();
      } catch (error) {
        console.error("Failed to load actions:", error);
        showToast({ severity: "error", detail: "Failed to load actions" });
      } finally {
        setIsLoading(false);
      }
    };

    loadActions();
  }, []);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const data = await saveActions(actions);
      setActions(data.actions || []);
      setMaxActionId(data.maxActionId || 0);
      setMaxObjectId(data.maxObjectId || 0);
      showToast({ severity: "success", detail: "Actions saved successfully" });
      markSaved();
    } catch (error) {
      console.error("Failed to save actions:", error);
      showToast({ severity: "error", detail: "Failed to save actions" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = () => {
    confirmDialog({
      message:
        "If you proceed, you will lose saved changes and have Security Journey default configurations restored.",
      header: "Are you sure?",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          const data = await restoreActions();
          setActions(data.actions || []);
          setMaxActionId(data.maxActionId || 0);
          setMaxObjectId(data.maxObjectId || 0);
          showToast({ severity: "success", detail: "Actions restored to defaults" });
          markSaved();
        } catch (error) {
          console.error("Failed to restore actions:", error);
          showToast({ severity: "error", detail: "Failed to restore actions" });
        }
      },
    });
  };

  const addAction = () => {
    const newId = maxActionId + 1;
    setActions([...actions, { id: newId, name: "", objects: [] }]);
    setMaxActionId(newId);
    markChanged();
  };

  const updateAction = (id: number, name: string) => {
    setActions(actions.map(action => (action.id === id ? { ...action, name } : action)));
    markChanged();
  };

  const deleteAction = (id: number) => {
    setActions(actions.filter(action => action.id !== id));
    markChanged();
  };

  const toggleAction = (id: number) => {
    setExpandedActions({
      ...expandedActions,
      [id]: !expandedActions[id],
    });
  };

  const addObject = (actionId: number) => {
    const newId = maxObjectId + 1;
    setActions(
      actions.map(action =>
        action.id === actionId
          ? { ...action, objects: [...action.objects, { id: newId, name: "" }] }
          : action
      )
    );
    setMaxObjectId(newId);
    markChanged();
  };

  const updateObject = (actionId: number, objectId: number, name: string) => {
    setActions(
      actions.map(action =>
        action.id === actionId
          ? {
              ...action,
              objects: action.objects.map(obj => (obj.id === objectId ? { ...obj, name } : obj)),
            }
          : action
      )
    );
    markChanged();
  };

  const deleteObject = (actionId: number, objectId: number) => {
    setActions(
      actions.map(action =>
        action.id === actionId
          ? { ...action, objects: action.objects.filter(obj => obj.id !== objectId) }
          : action
      )
    );
    markChanged();
  };

  const updateObjectsOrder = (actionId: number, newObjects: ActivityObject[]) => {
    setActions(
      actions.map(action => (action.id === actionId ? { ...action, objects: newObjects } : action))
    );
    markChanged();
  };

  const objectTemplate = (actionId: number, obj: ActivityObject) => {
    return (
      <div className="flex align-items-center gap-2 w-full">
        <InputText
          value={obj.name}
          onChange={e => updateObject(actionId, obj.id, e.target.value)}
          placeholder="Object name"
          className="flex-1"
          maxLength={45}
          autoFocus={obj.name === ""}
        />
        <Button
          icon="pi pi-trash"
          onClick={() => deleteObject(actionId, obj.id)}
          severity="danger"
          text
          rounded
          size="small"
        />
      </div>
    );
  };

  const actionTemplate = (action: ActivityAction) => {
    return (
      <div className="w-full">
        <div className="flex align-items-center gap-3 mb-2">
          <InputText
            value={action.name}
            onChange={e => updateAction(action.id, e.target.value)}
            placeholder="Action name"
            className="flex-1"
            maxLength={15}
            autoFocus={action.name === ""}
          />
          <Button
            icon={expandedActions[action.id] ? "pi pi-chevron-up" : "pi pi-chevron-down"}
            iconPos="right"
            onClick={() => {
              toggleAction(action.id);
              if (!expandedActions[action.id] && action.objects.length === 0) {
                addObject(action.id);
              }
            }}
            text
            className="w-40"
            label={
              expandedActions[action.id]
                ? "Hide objects"
                : action.objects.length > 0
                  ? "Edit objects"
                  : "Add objects"
            }
          />
          <Button
            icon="pi pi-trash"
            onClick={() => deleteAction(action.id)}
            severity="danger"
            text
          />
        </div>

        {expandedActions[action.id] && (
          <div className="ml-4 mt-3">
            <div className="font-semibold mb-2">Objects:</div>
            <OrderList
              dataKey="id"
              value={action.objects}
              onChange={e => updateObjectsOrder(action.id, e.value)}
              itemTemplate={obj => objectTemplate(action.id, obj)}
              dragdrop
              pt={{
                controls: { style: { justifyContent: "flex-start" } },
                list: { style: { maxHeight: "none", overflow: "visible" } },
              }}
            />
            <Button
              label="Add Object"
              icon="pi pi-plus"
              onClick={() => addObject(action.id)}
              severity="secondary"
              size="small"
              className="w-fit mt-2"
            />
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return <TabSkeleton rows={3} />;
  }

  return (
    <div className="p-4">
      <Card>
        <div className="mb-4">
          <p className="text-secondary mb-4">
            Configure actions and objects that can be performed in activities.
          </p>

          <OrderList
            dataKey="id"
            value={actions}
            onChange={e => {
              setActions(e.value);
              markChanged();
            }}
            itemTemplate={actionTemplate}
            dragdrop
            pt={{
              controls: { style: { justifyContent: "flex-start" } },
              list: { style: { maxHeight: "none", overflow: "visible" } },
            }}
          />

          <Button
            label="Add Action"
            icon="pi pi-plus"
            onClick={addAction}
            severity="secondary"
            className="w-fit mt-3"
          />
        </div>

        <div className="flex gap-2 mt-4 justify-end">
          <Button label="Restore Defaults" onClick={handleRestore} severity="secondary" />
          <Button
            label={isSaving ? "Saving..." : "Save"}
            onClick={() => void handleSave()}
            disabled={isSaving}
          />
        </div>
      </Card>
    </div>
  );
}
